import type { CdnInfo } from 'typedefs/common/CdnInfo'

import type { CdfEvent as ProtoCdfEvent } from '@cash-web/protos/squareup/cash/analytics/cdfEvent.pb'
import { Campaign } from '@cash-web/protos/squareup/cash/cdpproxy/api/campaign.pb'
import type { MessageContext } from '@cash-web/protos/squareup/cash/cdpproxy/api/message_context.pb'
import { OperatingSystemName } from '@cash-web/protos/squareup/cash/cdpproxy/api/operating_system.pb'
import logger from '@cash-web/shared/util-logger'
import { redactTokensRecursive } from '@cash-web/shared/util-redaction'
import { uuidv4 } from '@cash-web/shared-util-formatting'
import { getDeviceInfo } from '@cash-web/shared-util-user-agent'
import type { Event } from '@squareup/customer-data-events-web'

import { isBatchingEnabled } from './batchingFlag'
import { PacketBatcher } from './packetBatcher'

// Declaring this here to avoid type error for the 'cdn' property.
// Most apps will have this already defined from the OverridableTemplateVariables.d.ts
// however apps like help center do not use template variables so that typing will not be applicable.
// The code will fallback to just being undefined in that case.
declare global {
  interface Window {
    cdn: CdnInfo
  }
}

export interface LogEventParams {
  applicationName?: string
  event: Event
  region?: string
  customerToken?: string
  hostName?: string
  sendGate?: () => boolean | Promise<boolean>
}

export interface Packet {
  context: MessageContext
  track: {
    event_name: string
    properties: string
  }
  message_uuid: string
  timestamp_since_epoch_millis: number
  customer_token?: string
}

export interface AnalyticsRequest {
  analytics_messages: Packet[]
}

type DeliveryMode = 'default' | 'immediate'

const batcher = new PacketBatcher<Packet>()
const sendGates = new WeakMap<Packet, NonNullable<LogEventParams['sendGate']>>()
let keepaliveBytesInFlight = 0
let isLifecycleFlushRegistered = false
let isPageUnloading = false

function registerLifecycleFlush() {
  if (isLifecycleFlushRegistered || typeof window === 'undefined') {
    return
  }
  isLifecycleFlushRegistered = true
  // Preload so lifecycle flushes do not wait for the transport to download.
  void loadFetch().catch(() => undefined)
  window.addEventListener('pagehide', () => {
    isPageUnloading = true
    batcher.flush()
  })
  window.addEventListener('pageshow', () => {
    isPageUnloading = false
  })
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      batcher.flush()
    }
  })
}

/** Sends every pending packet now without waiting for the requests. */
export function flushPendingEvents(): void {
  batcher.flush()
}

/** Drops pending packets and timers. For tests. */
export function resetPendingEvents(): void {
  batcher.reset()
}

// Fetch rejects keepalive request bodies over 64 KiB, so stay under it
const MAX_KEEPALIVE_BODY_BYTES = 60_000
const encoder = new TextEncoder()

function bodySize(packets: readonly Packet[]): number {
  return encoder.encode(JSON.stringify({ analytics_messages: packets })).byteLength
}

// Defer import until client side render
const loadFetch = () => import('@cash-web/shared-util-fetch')

function splitByBodySize(packets: readonly Packet[]): Packet[][] {
  const chunks: Packet[][] = []
  let current: Packet[] = []
  packets.forEach(packet => {
    if (current.length > 0 && bodySize([...current, packet]) > MAX_KEEPALIVE_BODY_BYTES) {
      chunks.push(current)
      current = []
    }
    current.push(packet)
  })
  if (current.length > 0) {
    chunks.push(current)
  }
  return chunks
}

async function sendPackets(endpoint: string, packets: readonly Packet[], immediatePacket?: Packet): Promise<void> {
  const { post } = await loadFetch()

  async function dispatch(permitted: readonly Packet[]): Promise<void> {
    let chunks = splitByBodySize(permitted)
    let immediateChunk = immediatePacket ? chunks.find(chunk => chunk.includes(immediatePacket)) : undefined
    const availableBytes = MAX_KEEPALIVE_BODY_BYTES - keepaliveBytesInFlight
    if (
      immediatePacket &&
      immediateChunk &&
      bodySize(immediateChunk) > availableBytes &&
      bodySize([immediatePacket]) <= availableBytes
    ) {
      // Older packets must not push an immediate event that fits onto cancellable fetch.
      chunks = splitByBodySize(permitted.filter(packet => packet !== immediatePacket))
      immediateChunk = [immediatePacket]
    }
    const orderedChunks = immediateChunk
      ? [immediateChunk, ...chunks.filter(chunk => chunk !== immediateChunk)]
      : chunks
    let immediateRequest: Promise<unknown> | undefined
    const requests = orderedChunks.map(chunk => {
      const bytes = bodySize(chunk)
      const keepalive = keepaliveBytesInFlight + bytes <= MAX_KEEPALIVE_BODY_BYTES
      if (keepalive) {
        keepaliveBytesInFlight += bytes
      }
      // Overflow uses normal fetch so an older request cannot block delivery or exhaust the shared budget.
      const request = post(endpoint, { analytics_messages: chunk } as AnalyticsRequest, { keepalive }).finally(() => {
        if (keepalive) {
          keepaliveBytesInFlight -= bytes
        }
      })
      if (chunk === immediateChunk) {
        immediateRequest = request
      }
      return request.catch(() => undefined)
    })
    if (immediateChunk) {
      await immediateRequest
    } else {
      await Promise.all(requests)
    }
  }

  let ready: Packet[] = []
  let scheduledDispatch: Promise<void> | undefined
  function enqueueReady(packet: Packet): Promise<void> {
    ready.push(packet)
    if (!scheduledDispatch) {
      // Gates that resolve together still share a batch without waiting for pending gates.
      scheduledDispatch = Promise.resolve().then(() => {
        const permitted = ready
        ready = []
        scheduledDispatch = undefined
        return dispatch(permitted)
      })
    }
    return scheduledDispatch
  }

  const deliveries = packets.map(async packet => {
    const gate = sendGates.get(packet)
    let permitted: boolean
    try {
      permitted = await (!gate || gate())
    } catch {
      return
    }
    if (permitted) {
      await enqueueReady(packet)
    }
  })
  deliveries.forEach(delivery => void delivery.catch(() => undefined))
  if (immediatePacket) {
    await deliveries[packets.indexOf(immediatePacket)]
  } else {
    await Promise.all(deliveries)
  }
}

export function toClientCdfEvent(cdfEvent?: ProtoCdfEvent, extraParameters?: Event['parameters']): Event | void {
  if (!cdfEvent) {
    return
  }
  if (!cdfEvent.name) {
    logger.error(new Error('CDF event name is required, event will not be logged'))
    return
  }
  const values = cdfEvent.parameters?.values ?? {}
  return {
    name: cdfEvent.name,
    parameters: {
      ...values,
      ...extraParameters,
      occurred_at: String(Date.now()),
    },
  }
}

async function logEventInternal(
  { applicationName, event, region, customerToken, hostName, sendGate }: LogEventParams,
  mode: DeliveryMode
): Promise<void> {
  const eventContext = getAnalyticsEventContext(applicationName, region)
  redactTokensRecursive(event)
  redactTokensRecursive(eventContext)

  const packet: Packet = {
    context: eventContext,
    track: {
      event_name: event.name,
      properties: JSON.stringify(event.parameters),
    },
    message_uuid: uuidv4(),
    timestamp_since_epoch_millis: Date.now(),
    customer_token: customerToken,
  }

  if (sendGate && !(await sendGate())) {
    return
  }
  if (sendGate) {
    sendGates.set(packet, sendGate)
  }

  // Note: the app not necessarily sitting in the same domain, hostname will need to be configurable
  const endpoint = `${hostName ?? ''}/2.0/cash/web/cdp`

  registerLifecycleFlush()
  const send = (packets: readonly Packet[]) => sendPackets(endpoint, packets, mode === 'immediate' ? packet : undefined)
  if (mode === 'immediate') {
    await batcher.enqueueImmediately(endpoint, packet, send)
  } else {
    batcher.enqueue(endpoint, packet, send)
    if (isPageUnloading || document.visibilityState === 'hidden' || !isBatchingEnabled()) {
      // Consent rechecks can create more events after a lifecycle flush detaches its queue.
      batcher.flush(endpoint)
    }
  }
}

export function logEvent(params: LogEventParams): void {
  void logEventInternal(params, 'default').catch(() => undefined)
}

export function logEventImmediately(params: LogEventParams): Promise<void> {
  return logEventInternal(params, 'immediate')
}

// Gets the referrer as a Location object
function getReferrer(): HTMLAnchorElement | undefined {
  if (document.referrer) {
    const parser = document.createElement('a')
    parser.href = document.referrer
    return parser
  }
  return undefined
}

// Gets the initial_referrer from the initial_referrer cookie
function getInitialReferrer(): string | undefined {
  const initialReferrerCookie = document.cookie
    .split('; ')
    .find(row => row.startsWith('initial_referrer='))
    ?.split('=')[1]

  return initialReferrerCookie ? decodeURIComponent(initialReferrerCookie) : undefined
}

/**
 * We need to append 'Web' to iOS and Android OS to differentiate between Native Android/iOS events
 * Defaults to 'Web' for non-major browsers
 **/
export function getOperatingSystemName(name?: typeof OperatingSystemName | string) {
  // The string should be uppercase and spaces replaced with underscores
  const normalizeString = name?.toString().toUpperCase().split(' ').join('_')
  switch (normalizeString) {
    case OperatingSystemName.ANDROID:
      return OperatingSystemName.ANDROID_WEB
    case OperatingSystemName.IOS:
      return OperatingSystemName.IOS_WEB
    default: {
      const osName = OperatingSystemName[normalizeString as OperatingSystemName]
      return osName ? osName : OperatingSystemName.WEB
    }
  }
}

export function getUtmParams(location: Location): Campaign {
  const params = new URL(location.href).searchParams
  return {
    source: params.get('utm_source') || '',
    medium: params.get('utm_medium') || '',
    name: params.get('utm_name') || '',
    term: params.get('utm_term') || '',
    content: params.get('utm_content') || '',
  }
}

export function getAnalyticsEventContext(applicationName?: string, region?: string): MessageContext {
  const { browser, device, os } = getDeviceInfo()
  const location = window.location
  const referrer = getReferrer()
  const initialReferrer = getInitialReferrer()

  return {
    device: {
      model: device.model,
      manufacturer: device.vendor,
      type: device.type,
      screen_density_dpi: window.devicePixelRatio.toString() || '1',
      screen_height: window.screen.height,
      screen_width: window.screen.width,
    },
    operating_system: {
      name: getOperatingSystemName(os.name),
      version: os.version || 'unknown',
    },
    browser: {
      height: window.innerHeight,
      width: window.innerWidth,
      major_version: browser.major,
      name: browser.name,
      version: browser.version,
    },
    locale: navigator.language,
    user_agent: navigator.userAgent,
    location: {
      country: region,
    },
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    page: {
      path: location.pathname,
      referrer: referrer?.toString(),
      initial_referrer: initialReferrer,
      title: '', // This is redacted to prevent PII from leaking
      url: location.href,
      search: location.search,
    },
    campaign: getUtmParams(location),
    application: {
      name: applicationName || 'cash-web',
      // version must be semver compliant where patch is optional, on web the version is a gitsha
      // so we default to 1.0 and use the prerelease identifer in the semver spec to include the gitsha
      version: window.cdn?.version ? `1.0-${window.cdn.version}` : undefined,
      namespace: 'cash-web',
    },
  }
}
